<template>
  <div class="monetize-management">
    <el-tabs v-model="activeTab">
      <el-tab-pane label="变现渠道管理" name="channels">
        <div class="tab-header">
          <h2>变现渠道管理</h2>
          <el-button type="primary" @click="openChannelDialog()">
            <el-icon><Plus /></el-icon> 添加渠道
          </el-button>
        </div>
        
        <div v-if="channelsLoading" class="loading-container">
          <el-skeleton :rows="5" animated />
        </div>
        
        <div v-else-if="channelsError" class="error-container">
          <el-result
            icon="error"
            title="获取变现渠道列表失败"
            sub-title="请稍后再试或联系管理员"
          >
            <template #extra>
              <el-button type="primary" @click="fetchChannels">重新加载</el-button>
            </template>
          </el-result>
        </div>
        
        <div v-else-if="channels.length === 0" class="empty-container">
          <el-empty description="暂无变现渠道，点击上方按钮添加" />
        </div>
        
        <div v-else class="channels-table">
          <el-table
            :data="channels"
            border
            stripe
            style="width: 100%"
            :default-sort="{ prop: 'category_title', order: 'ascending' }"
          >
            <el-table-column prop="id" label="ID" width="80" />
            <el-table-column label="图标" width="100">
              <template #default="scope">
                <div class="channel-icon">
                  <img 
                    :src="scope.row.icon_url || '/default-channel-icon.png'" 
                    :alt="scope.row.name"
                    @error="handleImageError"
                  >
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="category_title" label="分类" width="150" sortable />
            <el-table-column prop="name" label="名称" width="180" sortable />
            <el-table-column prop="description" label="简介">
              <template #default="scope">
                <div class="description-cell">
                  {{ scope.row.description || '暂无简介' }}
                </div>
              </template>
            </el-table-column>
            <el-table-column label="链接" width="100">
              <template #default="scope">
                <el-button 
                  v-if="scope.row.link" 
                  type="primary" 
                  size="small" 
                  circle
                  @click="openLink(scope.row.link)"
                >
                  <el-icon><Link /></el-icon>
                </el-button>
                <span v-else>-</span>
              </template>
            </el-table-column>
            <el-table-column prop="sort_order" label="排序" width="100" sortable />
            <el-table-column label="状态" width="100">
              <template #default="scope">
                <el-tag :type="scope.row.is_active ? 'success' : 'info'">
                  {{ scope.row.is_active ? '已启用' : '已禁用' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="200" fixed="right">
              <template #default="scope">
                <el-button 
                  type="primary" 
                  size="small" 
                  @click="openChannelDialog(scope.row)"
                >
                  编辑
                </el-button>
                <el-button 
                  type="danger" 
                  size="small" 
                  @click="confirmDeleteChannel(scope.row)"
                >
                  删除
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-tab-pane>
      
      <el-tab-pane label="分类管理" name="categories">
        <div class="tab-header">
          <h2>分类管理</h2>
          <el-button type="primary" @click="openCategoryDialog()">
            <el-icon><Plus /></el-icon> 添加分类
          </el-button>
        </div>
        
        <div v-if="categoriesLoading" class="loading-container">
          <el-skeleton :rows="5" animated />
        </div>
        
        <div v-else-if="categoriesError" class="error-container">
          <el-result
            icon="error"
            title="获取分类列表失败"
            sub-title="请稍后再试或联系管理员"
          >
            <template #extra>
              <el-button type="primary" @click="fetchCategories">重新加载</el-button>
            </template>
          </el-result>
        </div>
        
        <div v-else-if="categories.length === 0" class="empty-container">
          <el-empty description="暂无分类，点击上方按钮添加" />
        </div>
        
        <div v-else class="categories-table">
          <el-table
            :data="categories"
            border
            stripe
            style="width: 100%"
            :default-sort="{ prop: 'sort_order', order: 'ascending' }"
          >
            <el-table-column prop="id" label="ID" width="80" />
            <el-table-column prop="title" label="标题" sortable />
            <el-table-column prop="sort_order" label="排序" width="100" sortable />
            <el-table-column label="操作" width="200" fixed="right">
              <template #default="scope">
                <el-button 
                  type="primary" 
                  size="small" 
                  @click="openCategoryDialog(scope.row)"
                >
                  编辑
                </el-button>
                <el-button 
                  type="danger" 
                  size="small" 
                  @click="confirmDeleteCategory(scope.row)"
                >
                  删除
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-tab-pane>
    </el-tabs>
    
    <!-- 渠道编辑对话框 -->
    <el-dialog
      v-model="channelDialogVisible"
      :title="currentChannel.id ? '编辑变现渠道' : '添加变现渠道'"
      width="900px"
      :close-on-click-modal="false"
    >
      <el-form 
        ref="channelFormRef"
        :model="channelForm"
        :rules="channelRules"
        label-width="100px"
      >
        <el-form-item label="所属分类" prop="category_id">
          <el-select v-model="channelForm.category_id" placeholder="请选择分类" style="width: 100%">
            <el-option
              v-for="item in categories"
              :key="item.id"
              :label="item.title"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        
        <el-form-item label="名称" prop="name">
          <el-input v-model="channelForm.name" placeholder="请输入名称" />
        </el-form-item>
        
        <el-form-item label="简介" prop="description">
          <el-input 
            v-model="channelForm.description" 
            type="textarea" 
            :rows="2"
            placeholder="请输入简介（非必填）" 
          />
        </el-form-item>
        
        <el-form-item label="链接" prop="link">
          <el-input v-model="channelForm.link" placeholder="请输入外部链接（非必填）" />
        </el-form-item>
        
        <el-form-item label="图标" prop="icon">
          <div class="icon-upload">
            <el-upload
              class="avatar-uploader"
              :action="null"
              :http-request="uploadIcon"
              :show-file-list="false"
              accept="image/*"
              :before-upload="beforeIconUpload"
            >
              <img 
                v-if="iconPreview" 
                :src="iconPreview" 
                class="avatar" 
                alt="图标预览"
              />
              <el-icon v-else class="avatar-uploader-icon"><Plus /></el-icon>
            </el-upload>
            <div class="upload-tip">
              点击上传图标<br>支持JPG/PNG/SVG等格式，尺寸不超过2MB
            </div>
          </div>
        </el-form-item>
        
        <el-form-item label="详情内容" prop="detail_content">
          <RichTextEditor 
            v-model="channelForm.detail_content" 
            placeholder="请输入详情内容（支持文字和图片）" 
            height="350px"
          />
          <div class="editor-tips">
            <p><i>支持图文混排，可以直接粘贴Word文档内容或拖拽图片到编辑器中</i></p>
            <p><i>支持从本地上传图片，最大支持2MB</i></p>
          </div>
        </el-form-item>
        
        <el-form-item label="排序顺序" prop="sort_order">
          <el-input-number v-model="channelForm.sort_order" :min="0" :max="9999" />
        </el-form-item>
        
        <el-form-item label="状态" prop="is_active">
          <el-switch 
            v-model="channelForm.is_active" 
            active-text="启用" 
            inactive-text="禁用"
          />
        </el-form-item>
      </el-form>
      
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="channelDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="submitChannelForm" :loading="submitLoading">
            确定
          </el-button>
        </span>
      </template>
    </el-dialog>
    
    <!-- 分类编辑对话框 -->
    <el-dialog
      v-model="categoryDialogVisible"
      :title="currentCategory.id ? '编辑分类' : '添加分类'"
      width="500px"
      :close-on-click-modal="false"
    >
      <el-form 
        ref="categoryFormRef"
        :model="categoryForm"
        :rules="categoryRules"
        label-width="80px"
      >
        <el-form-item label="标题" prop="title">
          <el-input v-model="categoryForm.title" placeholder="请输入分类标题" />
        </el-form-item>
        
        <el-form-item label="排序" prop="sort_order">
          <el-input-number v-model="categoryForm.sort_order" :min="0" :max="9999" />
        </el-form-item>
      </el-form>
      
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="categoryDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="submitCategoryForm" :loading="submitLoading">
            确定
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Plus, Link } from '@element-plus/icons-vue';
import { useRouter } from 'vue-router';
import { useUserStore } from '@/stores/user';
import RichTextEditor from '@/components/editor/RichTextEditor.vue';
import { 
  getMonetizeCategories, 
  createMonetizeCategory, 
  updateMonetizeCategory, 
  deleteMonetizeCategory,
  getAllMonetizeChannels,
  createMonetizeChannel,
  updateMonetizeChannel,
  deleteMonetizeChannel
} from '@/api/monetize';

// 标签页控制
const activeTab = ref('channels');

// 渠道列表相关
const channels = ref([]);
const channelsLoading = ref(false);
const channelsError = ref(false);

// 分类列表相关
const categories = ref([]);
const categoriesLoading = ref(false);
const categoriesError = ref(false);

// 渠道表单相关
const channelDialogVisible = ref(false);
const channelFormRef = ref(null);
const currentChannel = ref({});
const iconFile = ref(null);
const iconPreview = ref('');
const channelForm = ref({
  category_id: '',
  name: '',
  description: '',
  link: '',
  detail_content: '',
  sort_order: 0,
  is_active: true
});

// 分类表单相关
const categoryDialogVisible = ref(false);
const categoryFormRef = ref(null);
const currentCategory = ref({});
const categoryForm = ref({
  title: '',
  sort_order: 0
});

// 表单规则
const channelRules = {
  category_id: [
    { required: true, message: '请选择所属分类', trigger: 'change' }
  ],
  name: [
    { required: true, message: '请输入名称', trigger: 'blur' },
    { min: 2, max: 50, message: '长度在2到50个字符之间', trigger: 'blur' }
  ],
  link: [
    { pattern: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w.-]*)*\/?$/, message: '请输入有效的URL链接', trigger: 'blur' }
  ],
  sort_order: [
    { required: true, message: '请输入排序顺序', trigger: 'blur' }
  ]
};

const categoryRules = {
  title: [
    { required: true, message: '请输入分类标题', trigger: 'blur' },
    { min: 2, max: 50, message: '长度在2到50个字符之间', trigger: 'blur' }
  ],
  sort_order: [
    { required: true, message: '请输入排序顺序', trigger: 'blur' }
  ]
};

// 提交状态
const submitLoading = ref(false);

// 添加router和userStore
const router = useRouter();
const userStore = useUserStore();

// 获取所有变现渠道
const fetchChannels = async () => {
  channelsLoading.value = true;
  channelsError.value = false;
  
  try {
    const response = await getAllMonetizeChannels();
    channels.value = response.data;
  } catch (err) {
    console.error('获取变现渠道列表失败:', err);
    channelsError.value = true;
    ElMessage.error('获取变现渠道列表失败，请稍后再试');
  } finally {
    channelsLoading.value = false;
  }
};

// 获取所有分类
const fetchCategories = async () => {
  categoriesLoading.value = true;
  categoriesError.value = false;
  
  try {
    const response = await getMonetizeCategories();
    categories.value = response.data;
  } catch (err) {
    console.error('获取分类列表失败:', err);
    categoriesError.value = true;
    ElMessage.error('获取分类列表失败，请稍后再试');
  } finally {
    categoriesLoading.value = false;
  }
};

// 打开渠道编辑对话框
const openChannelDialog = (channel) => {
  currentChannel.value = channel || {};
  
  if (channel) {
    // 编辑模式
    channelForm.value = {
      category_id: channel.category_id,
      name: channel.name,
      description: channel.description || '',
      link: channel.link || '',
      detail_content: channel.detail_content || '',
      sort_order: channel.sort_order,
      is_active: Boolean(channel.is_active)
    };
    iconPreview.value = channel.icon_url;
  } else {
    // 新增模式
    channelForm.value = {
      category_id: '',
      name: '',
      description: '',
      link: '',
      detail_content: '',
      sort_order: 0,
      is_active: true
    };
    iconPreview.value = '';
    iconFile.value = null;
  }
  
  channelDialogVisible.value = true;
};

// 打开分类编辑对话框
const openCategoryDialog = (category) => {
  currentCategory.value = category || {};
  
  if (category) {
    // 编辑模式
    categoryForm.value = {
      title: category.title,
      sort_order: category.sort_order
    };
  } else {
    // 新增模式
    categoryForm.value = {
      title: '',
      sort_order: 0
    };
  }
  
  categoryDialogVisible.value = true;
};

// 上传图标前的验证
const beforeIconUpload = (file) => {
  const isLt2M = file.size / 1024 / 1024 < 2;
  if (!isLt2M) {
    ElMessage.error('图标大小不能超过2MB!');
    return false;
  }
  
  iconFile.value = file;
  return true;
};

// 自定义上传图标
const uploadIcon = (options) => {
  const file = options.file;
  iconFile.value = file;
  
  // 创建本地预览
  const reader = new FileReader();
  reader.onload = (e) => {
    iconPreview.value = e.target.result;
  };
  reader.readAsDataURL(file);
};

// 处理图片加载错误
const handleImageError = (e) => {
  e.target.src = '/default-channel-icon.png';
};

// 打开外部链接
const openLink = (url) => {
  window.open(url, '_blank');
};

// 提交渠道表单
const submitChannelForm = async () => {
  if (!channelFormRef.value) return;
  
  try {
    await channelFormRef.value.validate();
    
    submitLoading.value = true;
    
    // 准备表单数据
    const formData = new FormData();
    Object.keys(channelForm.value).forEach(key => {
      if (key === 'is_active') {
        formData.append(key, channelForm.value[key] ? 'true' : 'false');
      } else {
        formData.append(key, channelForm.value[key]);
      }
    });
    
    // 如果有新图标，添加到表单数据
    if (iconFile.value) {
      formData.append('icon', iconFile.value);
    }
    
    let response;
    if (currentChannel.value.id) {
      // 更新渠道
      response = await updateMonetizeChannel(currentChannel.value.id, formData);
      ElMessage.success('变现渠道更新成功');
    } else {
      // 创建新渠道
      response = await createMonetizeChannel(formData);
      ElMessage.success('变现渠道添加成功');
    }
    
    // 关闭对话框并刷新列表
    channelDialogVisible.value = false;
    fetchChannels();
  } catch (err) {
    console.error('提交变现渠道表单失败:', err);
    ElMessage.error(err.response?.data?.message || '操作失败，请重试');
  } finally {
    submitLoading.value = false;
  }
};

// 提交分类表单
const submitCategoryForm = async () => {
  if (!categoryFormRef.value) return;
  
  try {
    await categoryFormRef.value.validate();
    
    submitLoading.value = true;
    
    if (currentCategory.value.id) {
      // 更新分类
      await updateMonetizeCategory(currentCategory.value.id, categoryForm.value);
      ElMessage.success('分类更新成功');
    } else {
      // 创建新分类
      await createMonetizeCategory(categoryForm.value);
      ElMessage.success('分类添加成功');
    }
    
    // 关闭对话框并刷新列表
    categoryDialogVisible.value = false;
    fetchCategories();
    
    // 如果正在编辑渠道，也需要刷新分类下拉列表
    if (channelDialogVisible.value) {
      fetchCategories();
    }
  } catch (err) {
    console.error('提交分类表单失败:', err);
    ElMessage.error(err.response?.data?.message || '操作失败，请重试');
  } finally {
    submitLoading.value = false;
  }
};

// 确认删除渠道
const confirmDeleteChannel = (channel) => {
  ElMessageBox.confirm(
    `确定要删除"${channel.name}"吗？此操作不可恢复。`,
    '删除确认',
    {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }
  )
    .then(async () => {
      try {
        await deleteMonetizeChannel(channel.id);
        ElMessage.success('删除成功');
        fetchChannels();
      } catch (err) {
        console.error('删除变现渠道失败:', err);
        ElMessage.error(err.response?.data?.message || '删除失败，请重试');
      }
    })
    .catch(() => {
      // 用户取消删除
    });
};

// 确认删除分类
const confirmDeleteCategory = (category) => {
  // 检查是否有引用该分类的渠道
  const hasReferences = channels.value.some(channel => channel.category_id === category.id);
  
  if (hasReferences) {
    ElMessage.warning('该分类下存在渠道，无法删除。请先删除或移动该分类下的所有渠道。');
    return;
  }
  
  ElMessageBox.confirm(
    `确定要删除"${category.title}"分类吗？此操作不可恢复。`,
    '删除确认',
    {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }
  )
    .then(async () => {
      try {
        await deleteMonetizeCategory(category.id);
        ElMessage.success('删除成功');
        fetchCategories();
      } catch (err) {
        console.error('删除分类失败:', err);
        ElMessage.error(err.response?.data?.message || '删除失败，请重试');
      }
    })
    .catch(() => {
      // 用户取消删除
    });
};

// 组件挂载时获取数据
onMounted(() => {
  // 检查用户登录状态
  const token = localStorage.getItem('token');
  const userStr = localStorage.getItem('user');
  
  console.log('当前登录状态:', {
    hasToken: !!token,
    tokenPrefix: token ? token.substring(0, 15) + '...' : 'none',
    hasUserData: !!userStr,
    userStore: {
      isLoggedIn: userStore.isLoggedIn,
      isAdmin: userStore.isAdmin,
      username: userStore.user?.username
    }
  });
  
  if (!token || !userStr) {
    ElMessage.error('您未登录或登录已过期，请重新登录');
    router.push('/login');
    return;
  }
  
  try {
    const userData = JSON.parse(userStr);
    if (userData.role !== 'admin') {
      ElMessage.error('您没有管理员权限');
      router.push('/');
      return;
    }
  } catch (err) {
    console.error('解析用户数据失败:', err);
    ElMessage.error('登录状态异常，请重新登录');
    router.push('/login');
    return;
  }
  
  // 状态正常，获取数据
  fetchChannels();
  fetchCategories();
});
</script>

<style scoped>
.monetize-management {
  padding: 20px;
}

.tab-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.tab-header h2 {
  margin: 0;
  font-size: 18px;
}

.loading-container, 
.error-container, 
.empty-container {
  padding: 40px 0;
  display: flex;
  justify-content: center;
}

.channel-icon {
  width: 40px;
  height: 40px;
  border-radius: 4px;
  overflow: hidden;
  margin: 0 auto;
}

.channel-icon img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.description-cell {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
}

.icon-upload {
  display: flex;
  align-items: center;
}

.avatar-uploader {
  width: 100px;
  height: 100px;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 20px;
}

.avatar-uploader:hover {
  border-color: #409EFF;
}

.avatar-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.upload-tip {
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}

.editor-tips {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}

.editor-tips p {
  margin: 2px 0;
}

.dialog-footer {
  width: 100%;
  display: flex;
  justify-content: flex-end;
}
</style> 